import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Button } from './button';
import { Toggle } from './toggle';
import { Separator } from './separator';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Link as LinkIcon,
    Unlink,
    Code,
    Quote,
    Undo,
    Redo,
    Palette,
    Highlighter,
    Eye,
    Code as CodeIcon,
} from 'lucide-react';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    className?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Start writing...',
    label,
    className = '',
}) => {
    const [isPreview, setIsPreview] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-600 underline cursor-pointer',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Underline,
            TextStyle,
            Color,
            Highlight.configure({
                multicolor: true,
            }),
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4',
            },
        },
    });

    // Sync editor content when value prop changes externally
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
        }
    }, [value, editor]);

    if (!editor) {
        return null;
    }

    const addLink = () => {
        if (linkUrl) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
            setLinkUrl('');
            setShowLinkInput(false);
        }
    };

    const removeLink = () => {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
    };

    const setTextColor = (color: string) => {
        editor.chain().focus().setColor(color).run();
    };

    const setHighlight = (color: string) => {
        editor.chain().focus().toggleHighlight({ color }).run();
    };

    const colorOptions = [
        { name: 'Default', value: '#000000' },
        { name: 'Red', value: '#ef4444' },
        { name: 'Green', value: '#22c55e' },
        { name: 'Blue', value: '#3b82f6' },
        { name: 'Yellow', value: '#eab308' },
        { name: 'Purple', value: '#a855f7' },
        { name: 'Gray', value: '#6b7280' },
    ];

    const highlightOptions = [
        { name: 'Yellow', value: '#fef3c7' },
        { name: 'Green', value: '#dcfce7' },
        { name: 'Blue', value: '#dbeafe' },
        { name: 'Pink', value: '#fce7f3' },
    ];

    return (
        <div className={`space-y-2 ${className}`}>
            {label && <label className="text-sm font-medium">{label}</label>}

            {/* Toolbar */}
            <div className="flex items-center gap-1 p-2 border rounded-t-md bg-muted/50 flex-wrap">
                {/* Basic formatting */}
                <div className="flex items-center gap-1">
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('bold')}
                        onPressedChange={() => editor.chain().focus().toggleBold().run()}
                    >
                        <Bold className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('italic')}
                        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
                    >
                        <Italic className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('underline')}
                        onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
                    >
                        <UnderlineIcon className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('strike')}
                        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
                    >
                        <Strikethrough className="h-4 w-4" />
                    </Toggle>
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* Lists */}
                <div className="flex items-center gap-1">
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('bulletList')}
                        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
                    >
                        <List className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('orderedList')}
                        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
                    >
                        <ListOrdered className="h-4 w-4" />
                    </Toggle>
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* Alignment */}
                <div className="flex items-center gap-1">
                    <Toggle
                        size="sm"
                        pressed={editor.isActive({ textAlign: 'left' })}
                        onPressedChange={() => editor.chain().focus().setTextAlign('left').run()}
                    >
                        <AlignLeft className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive({ textAlign: 'center' })}
                        onPressedChange={() => editor.chain().focus().setTextAlign('center').run()}
                    >
                        <AlignCenter className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive({ textAlign: 'right' })}
                        onPressedChange={() => editor.chain().focus().setTextAlign('right').run()}
                    >
                        <AlignRight className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive({ textAlign: 'justify' })}
                        onPressedChange={() => editor.chain().focus().setTextAlign('justify').run()}
                    >
                        <AlignJustify className="h-4 w-4" />
                    </Toggle>
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* Links */}
                <div className="flex items-center gap-1">
                    {!showLinkInput ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowLinkInput(true)}
                            className="h-8 w-8 p-0"
                        >
                            <LinkIcon className="h-4 w-4" />
                        </Button>
                    ) : (
                        <div className="flex items-center gap-1">
                            <input
                                type="url"
                                placeholder="Enter URL..."
                                value={linkUrl}
                                onChange={e => setLinkUrl(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        addLink();
                                    } else if (e.key === 'Escape') {
                                        setShowLinkInput(false);
                                        setLinkUrl('');
                                    }
                                }}
                                className="h-8 px-2 text-sm border rounded"
                                autoFocus
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={addLink}
                                className="h-8 px-2"
                            >
                                Add
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setShowLinkInput(false);
                                    setLinkUrl('');
                                }}
                                className="h-8 px-2"
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                    {editor.isActive('link') && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removeLink}
                            className="h-8 w-8 p-0"
                        >
                            <Unlink className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* Colors */}
                <div className="flex items-center gap-1">
                    <div className="relative group">
                        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Palette className="h-4 w-4" />
                        </Button>
                        <div className="absolute top-full left-0 mt-1 bg-white border rounded-md shadow-lg p-2 z-10 hidden group-hover:block">
                            <div className="grid grid-cols-4 gap-1">
                                {colorOptions.map(color => (
                                    <button
                                        key={color.value}
                                        type="button"
                                        onClick={() => setTextColor(color.value)}
                                        className="w-6 h-6 rounded border"
                                        style={{ backgroundColor: color.value }}
                                        title={color.name}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="relative group">
                        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Highlighter className="h-4 w-4" />
                        </Button>
                        <div className="absolute top-full left-0 mt-1 bg-white border rounded-md shadow-lg p-2 z-10 hidden group-hover:block">
                            <div className="grid grid-cols-2 gap-1">
                                {highlightOptions.map(color => (
                                    <button
                                        key={color.value}
                                        type="button"
                                        onClick={() => setHighlight(color.value)}
                                        className="w-6 h-6 rounded border"
                                        style={{ backgroundColor: color.value }}
                                        title={color.name}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* Code and quotes */}
                <div className="flex items-center gap-1">
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('code')}
                        onPressedChange={() => editor.chain().focus().toggleCode().run()}
                    >
                        <Code className="h-4 w-4" />
                    </Toggle>
                    <Toggle
                        size="sm"
                        pressed={editor.isActive('blockquote')}
                        onPressedChange={() => editor.chain().focus().toggleBlockquote().run()}
                    >
                        <Quote className="h-4 w-4" />
                    </Toggle>
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* History */}
                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        className="h-8 w-8 p-0"
                    >
                        <Undo className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        className="h-8 w-8 p-0"
                    >
                        <Redo className="h-4 w-4" />
                    </Button>
                </div>

                <Separator orientation="vertical" className="h-6" />

                {/* Preview toggle */}
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsPreview(!isPreview)}
                    className="h-8 px-2"
                >
                    {isPreview ? <CodeIcon className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    {isPreview ? 'HTML' : 'Preview'}
                </Button>
            </div>

            {/* Editor/Preview */}
            {isPreview ? (
                <div className="border border-t-0 rounded-b-md bg-background">
                    <textarea
                        value={value}
                        onChange={e => {
                            const newValue = e.target.value;
                            onChange(newValue);
                            editor?.commands.setContent(newValue);
                        }}
                        className="w-full min-h-[200px] p-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-b-md resize-y"
                        placeholder="Edit HTML directly..."
                        spellCheck={false}
                    />
                </div>
            ) : (
                <div className="border border-t-0 rounded-b-md min-h-[200px] bg-background">
                    <EditorContent editor={editor} />
                </div>
            )}
        </div>
    );
};

export default RichTextEditor;
